import type { FC } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { AppBar, Box, Button, Divider, Hidden, IconButton, Toolbar, Typography, Stack } from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import PersonIcon from '@mui/icons-material/Person';
import { Theme } from '@mui/material/styles';
import createStyles from '@mui/styles/createStyles';
import makeStyles from '@mui/styles/makeStyles';
import { Logo } from './Logo';
import { accountService, UserRole } from '../account.service';

interface MainNavbarProps {
    onSidebarMobileOpen?: () => void;
    showMenu: boolean;
    customTitle?: string;
}

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        homeLink: {
            textDecoration: 'none',
        },
    })
);

export const MainNavbar: FC<MainNavbarProps> = ({ onSidebarMobileOpen, showMenu, customTitle }) => {
    const classes = useStyles();
    const navigate = useNavigate();
    const account = accountService.getAccount();

    const onSignIn = () => {
        if (account) {
            accountService.logout();
            navigate('/');
        } else {
            navigate('/account/login');
        }
    };

    const onProfile = () => {
        if (account) {
            switch (account.role) {
                case UserRole.Reseller:
                    navigate('/reseller/orders');
                    break;
                case UserRole.Admin:
                    navigate('/kb/');
                    break;
                default:
                    navigate('/client/orders');
                    break;
            }
        } else {
            window.open('https://lsoft.zendesk.com/hc/en-us/requests/new', '_blank');
        }
    };

    return (
        <AppBar
            elevation={0}
            sx={{
                color: 'text.secondary',
            }}
        >
            <Toolbar sx={{ minHeight: 64 }}>
                {showMenu && (
                    <Hidden mdUp>
                        <IconButton sx={{ color: 'white' }} onClick={onSidebarMobileOpen} size="large">
                            <MenuIcon fontSize="small" />
                        </IconButton>
                    </Hidden>
                )}
                <RouterLink to="/" className={classes.homeLink}>
                    <Stack direction="row" spacing={1} sx={{ textDecoration: 'none' }}>
                        <Logo />
                        <Typography
                            sx={{
                                caretColor: 'rgba(0,0,0,0)',
                                color: 'white',
                                whiteSpace: 'nowrap',
                                textDecoration: 'none',
                                lineHeight: '40px',
                            }}
                            variant="h6"
                        >
                            LSoft Technologies
                        </Typography>
                        <Typography
                            id="page-title"
                            sx={{
                                color: 'white',
                                whiteSpace: 'nowrap',
                                fontWeight: 100,
                                lineHeight: '40px',
                                display: { xs: 'none', md: 'block' },
                            }}
                            variant="h6"
                        >
                            |&nbsp; {customTitle ? customTitle : 'Customer Support'}
                        </Typography>
                    </Stack>
                </RouterLink>
                <Box sx={{ flexGrow: 1 }} />
                {showMenu && (
                    <Hidden mdDown>
                        <Button
                            sx={{
                                color: 'white',
                                marginLeft: 1,
                            }}
                            onClick={onProfile}
                        >
                            {account ? (account.role === UserRole.Admin ? 'Dashboard' : 'Profile') : 'Submit request'}
                        </Button>

                        <Divider
                            orientation="vertical"
                            sx={{
                                height: 32,
                                mx: 2,
                                backgroundColor: 'white',
                            }}
                        />
                        <Button
                            sx={{
                                color: 'white',
                                marginLeft: 1,
                            }}
                            startIcon={<PersonIcon />}
                            onClick={onSignIn}
                        >
                            {account ? 'Sign Out' : 'Sign In'}
                        </Button>
                    </Hidden>
                )}
            </Toolbar>
        </AppBar>
    );
};
